import {
    getAuth,
    signOut,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    UserInfo,
    updatePassword,
    updateProfile,
    sendEmailVerification,
    RecaptchaVerifier,
    PhoneAuthProvider,
    EmailAuthProvider,
    reauthenticateWithCredential,
} from 'firebase/auth';
import {getAnalytics, logEvent} from 'firebase/analytics';
import {createAsyncThunk} from '@reduxjs/toolkit';
import {load} from 'recaptcha-v3';
import {getApp} from 'firebase/app';
import {getFunctions, httpsCallable} from "firebase/functions";
import {updatePhoneNumber} from "@firebase/auth";
import {getFirestore, doc, getDoc, updateDoc, increment} from "firebase/firestore";
import firebase from "firebase/compat";
import {authenticated} from '../authSlice';

export type Credential = {
  email: string;
  password: string;
  remember?: boolean;
};

export const signin = createAsyncThunk('auth/signin', async (credential: Credential): Promise<void> => {
  const { email, password } = credential;
  const { user } = await signInWithEmailAndPassword(getAuth(), email, password);
  // Increment login count
  const userDocRef = doc(getFirestore(), `users/${user.uid}`);
  await updateDoc(userDocRef, { loginCount: increment(1) });
});

export const signup = createAsyncThunk(
  'auth/signup',
  async (payload: { email: string; password: string }): Promise<void> => {
    const { email, password } = payload;
    const auth = getAuth();
    const analytics = getAnalytics();
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(user);
    logEvent(analytics, 'signup');
  },
);

export const verifyEmail = createAsyncThunk('auth/verifyEmail', async (): Promise<void> => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    await sendEmailVerification(user);
  }
});

export const verifyPhoneCode = createAsyncThunk('auth/verifyPhoneCode', async (params: { verificationCode: string, verificationId: string }, { dispatch }): Promise<void> => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error('You must be logged in to verify your phone number. Please sign in and try again.');
    }

    try {
        const authCredential = PhoneAuthProvider.credential(params.verificationId, params.verificationCode);
        await updatePhoneNumber(user, authCredential);
        // Manually reload and update Redux so ensureAuth sees phoneNumber immediately
        await user.reload();
        dispatch(authenticated(getAuth().currentUser));
    } catch (error: any) {
        if (error?.code === 'auth/invalid-verification-code') {
            throw new Error('Invalid verification code. Please check and try again.');
        }
        if (error?.code === 'auth/code-expired') {
            throw new Error('Verification code expired. Please request a new code.');
        }
        if (error?.code === 'auth/user-token-expired') {
            throw new Error('Your session has expired. Please sign out, sign back in, and try again.');
        }
        throw error;
    }
});
// Creating the appVerifier
export const verifyPhone = createAsyncThunk('auth/verifyPhone', async (
    { phoneNumber, appVerifier }: { phoneNumber: string; appVerifier: RecaptchaVerifier }
): Promise<{ verificationId: string }> => {
    const auth = getAuth();
    const provider = new PhoneAuthProvider(auth);
    const verificationId = await provider.verifyPhoneNumber(phoneNumber, appVerifier);
    return { verificationId };
});


export const resetPassword = createAsyncThunk('auth/resetPassword', async (email: string): Promise<void> => {
  const auth = getAuth();
  const analytics = getAnalytics();
  await sendPasswordResetEmail(auth, email);
  logEvent(analytics, 'reset_password');
});

export const signout = createAsyncThunk('auth/signout', async (): Promise<void> => {
  const auth = getAuth();
  await signOut(auth);
});

export const changePassword = createAsyncThunk('auth/changePassword', async (data: { currentPassword: string; newPassword: string }): Promise<void> => {
    const auth = getAuth();
    const user = auth.currentUser!;

    try {
        // Re-authenticate user before password change
        const credential = EmailAuthProvider.credential(user.email!, data.currentPassword);
        await reauthenticateWithCredential(user, credential);

        // Now update the password
        await updatePassword(user, data.newPassword);
    } catch (error: any) {
        // Handle specific Firebase auth errors
        if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
            const customError = new Error('Current password is incorrect');
            customError.name = 'WRONG_PASSWORD';
            throw customError;
        } else if (error.code === 'auth/weak-password') {
            const customError = new Error('New password is too weak');
            customError.name = 'WEAK_PASSWORD';
            throw customError;
        } else if (error.code === 'auth/requires-recent-login') {
            const customError = new Error('Please sign in again to change your password');
            customError.name = 'REQUIRES_RECENT_LOGIN';
            throw customError;
        }
        // Re-throw other errors as-is
        throw error;
    }
});

export const updatePhotoURL = createAsyncThunk(
  'auth/updatePhoto',
  async (photoURL: string): Promise<Partial<UserInfo>> => {
    const auth = getAuth();
    await updateProfile(auth.currentUser!, { photoURL });
    return { photoURL };
  },
);

export const unsubscribe = createAsyncThunk('auth/unsubscribe', async ({ email }: { email: string }) => {
    const recaptchaSiteKey = process.env.GATSBY_RECAPTCHA_SITE_KEY;
    if (!recaptchaSiteKey) {
        return;
    }
    const recaptcha = await load(recaptchaSiteKey);
    const token = await recaptcha.execute('unsubscribe');

    const functions = getFunctions(getApp());

    await httpsCallable(
        functions,
        'unsubscribeFromEmails',
    )({
        email,
        recaptchaToken: token,
    });
});

export const verifiedUser = async () => {
  const functions = getFunctions(getApp());
  const ruserVerifiedtFn = httpsCallable(functions, 'userVerified');
  await ruserVerifiedtFn();
};

/**
 * Check if user has a temporary password that needs to be changed
 * Returns true if usersInternal/{uid} document exists with temporaryPassword
 */
export const hasTemporaryPassword = async (uid: string): Promise<boolean> => {
  try {
    const firestore = getFirestore();
    const docRef = doc(firestore, `usersInternal/${uid}`);
    const docSnap = await getDoc(docRef);
    const hasTempPassword = docSnap.exists() && Boolean(docSnap.data()?.temporaryPassword);
    return hasTempPassword;
  } catch (error) {
    console.error('[hasTemporaryPassword] Error reading usersInternal:', error);
    // If we can't read, assume no temp password (might be Firestore rules issue)
    return false;
  }
};
